import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Plus, Minus, X, Send, ImageIcon, Search, ShoppingCart } from "lucide-react";

const B2BPortal = () => {
  const { code } = useParams();
  const [entreprise, setEntreprise] = useState(null);
  const [remise, setRemise] = useState(0);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({
    contact_nom: "", contact_telephone: "", contact_email: "",
    adresse_livraison: "", ville: "", code_postal: "",
    date_souhaitee: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/b2b/${code}/produits`);
        if (cancelled) return;
        setEntreprise(res.data.entreprise);
        setRemise(res.data.remise_pct || 0);
        setProduits(res.data.produits || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Lien B2B invalide");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const filteredProduits = useMemo(() => {
    const q = query.trim().toLowerCase();
    return produits.filter(p => !q || (`${p.modele} ${p.couleur} ${p.sku}`).toLowerCase().includes(q));
  }, [produits, query]);

  const addToCart = (p) => {
    setCart((prev) => {
      const idx = prev.findIndex(x => x.sku === p.sku);
      const stockLimit = Math.max(0, p.stock_restant || 0);
      if (idx >= 0) {
        if (prev[idx].quantite >= stockLimit) {
          toast.error(`Stock max atteint (${stockLimit})`);
          return prev;
        }
        const next = [...prev];
        next[idx] = { ...next[idx], quantite: next[idx].quantite + 1, stock_max: stockLimit };
        return next;
      }
      if (stockLimit <= 0) {
        toast.error("Rupture de stock");
        return prev;
      }
      return [...prev, { sku: p.sku, modele: p.modele, couleur: p.couleur, image_url: p.image_url || "", prix_ht: p.prix_b2b_final_ht || 0, quantite: 1, stock_max: stockLimit }];
    });
  };
  const changeQty = (sku, delta) => setCart((prev) => prev.map(it => {
    if (it.sku !== sku) return it;
    const max = it.stock_max ?? 9999;
    const newQty = it.quantite + delta;
    if (newQty < 1) return it;
    if (newQty > max) { toast.error(`Stock max atteint (${max})`); return it; }
    return { ...it, quantite: newQty };
  }));
  const removeItem = (sku) => setCart((prev) => prev.filter(it => it.sku !== sku));

  const totalHT = cart.reduce((a, it) => a + (it.prix_ht || 0) * (it.quantite || 0), 0);
  const totalQte = cart.reduce((a, it) => a + (it.quantite || 0), 0);

  const submitOrder = async () => {
    if (cart.length === 0) { toast.error("Panier vide"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        produits: cart.map(it => ({ sku: it.sku, quantite: it.quantite })),
      };
      const res = await axios.post(`${API}/public/b2b/${code}/commande`, payload);
      setDone(res.data);
      setCart([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Accès refusé</h2>
            <p className="text-slate-600">{error}</p>
            <p className="text-xs text-slate-400">Contactez votre commercial pour obtenir un lien valide.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-3xl">✓</div>
            <h2 className="text-xl font-bold text-slate-800">Commande enregistrée</h2>
            <p className="text-slate-600">Numéro : <strong>#{done.numero_commande}</strong></p>
            <p className="text-slate-600">Total HT : <strong>{done.total_ht.toFixed(2)} €</strong></p>
            <p className="text-sm text-slate-500">{done.message}</p>
            <Button onClick={() => { setDone(null); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-new-order-btn">Nouvelle commande</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="b2b-portal-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-6 h-6 text-indigo-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-slate-800 truncate">{entreprise}</p>
              <p className="text-xs text-slate-500">Portail B2B TopCocoon{remise > 0 ? ` — Remise ${remise}%` : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 rounded-full px-3 py-1.5 text-sm font-semibold" data-testid="b2b-cart-badge">
            <ShoppingCart className="w-4 h-4" />
            {totalQte} · {totalHT.toFixed(0)} € HT
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — products */}
        <div className="lg:col-span-2 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher…" className="pl-9 bg-white" data-testid="b2b-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredProduits.map((p) => {
              const out = p.stock_restant <= 0;
              return (
                <button
                  key={p.id}
                  disabled={out}
                  onClick={() => addToCart(p)}
                  className={`flex flex-col rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm text-left transition ${out ? "opacity-40 cursor-not-allowed" : "hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-300"}`}
                  data-testid={`b2b-tile-${p.sku}`}
                >
                  <div className="aspect-square w-full bg-slate-50 flex items-center justify-center overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <div className="p-2 space-y-0.5">
                    <p className="font-semibold text-sm text-slate-800 truncate">{p.modele}</p>
                    <p className="text-xs text-slate-500 truncate">{p.couleur}</p>
                    <div className="pt-1 flex items-center justify-between">
                      <span className="text-sm font-bold text-indigo-600">{(p.prix_b2b_final_ht || 0).toFixed(2)} € HT</span>
                      {out && <span className="text-[10px] text-red-600">Rupture</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT — cart + form */}
        <div className="space-y-3">
          <Card>
            <CardContent className="pt-4 space-y-3">
              <p className="font-semibold text-slate-800">Votre commande</p>
              {cart.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">Cliquez sur un produit pour l&apos;ajouter.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cart.map((it) => (
                    <div key={it.sku} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2" data-testid={`b2b-cart-${it.sku}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{it.modele}</p>
                        <p className="text-xs text-slate-500 truncate">{it.couleur} · {it.prix_ht.toFixed(2)} € HT</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(it.sku, -1)}><Minus className="w-3 h-3" /></Button>
                        <span className="w-6 text-center text-sm font-semibold">{it.quantite}</span>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(it.sku, 1)}><Plus className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeItem(it.sku)}><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-3 flex justify-between items-baseline">
                <span className="text-slate-600 text-sm">Total HT</span>
                <span className="text-xl font-bold text-indigo-700" data-testid="b2b-total-ht">{totalHT.toFixed(2)} €</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 space-y-3">
              <p className="font-semibold text-slate-800">Coordonnées livraison</p>
              <div className="grid grid-cols-1 gap-2">
                <Input placeholder="Nom du contact" value={form.contact_nom} onChange={(e) => setForm({ ...form, contact_nom: e.target.value })} data-testid="b2b-contact-nom" />
                <Input placeholder="Téléphone" value={form.contact_telephone} onChange={(e) => setForm({ ...form, contact_telephone: e.target.value })} data-testid="b2b-contact-tel" />
                <Input placeholder="Email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="b2b-contact-email" />
                <Input placeholder="Adresse de livraison" value={form.adresse_livraison} onChange={(e) => setForm({ ...form, adresse_livraison: e.target.value })} data-testid="b2b-adresse" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="CP" value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} />
                  <Input placeholder="Ville" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Date souhaitée (optionnel)</Label>
                  <Input type="date" value={form.date_souhaitee} onChange={(e) => setForm({ ...form, date_souhaitee: e.target.value })} data-testid="b2b-date" />
                </div>
                <Input placeholder="Note (optionnel)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Button
                onClick={submitOrder}
                disabled={submitting || cart.length === 0}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
                data-testid="b2b-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Envoi…" : "Envoyer la commande"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default B2BPortal;
